import { useTranslation } from '@rhino-project/core/i18n';
import { Link } from '@tanstack/react-router';
import { Typography } from '@heroui/react';
import {
  BaseAuthedPage,
  MaxWidth,
  useBaseOwnerPath,
  ModelIndexSimple,
  ModelIndexTable
} from '@rhino-project/ui-heroui';
import {
  useBaseOwnerId,
  useBaseOwner,
  useHasRoleOf
} from '@rhino-project/core/hooks';
import { Icon } from '@iconify/react';
import { useCurrentTeam, usePlanningSeasons } from '../hooks/ots';
import SeasonSidebarForm from '../components/ots/SeasonSidebarForm';

import { Button, Card, useOverlayState } from '@heroui/react';

import type { RhinoOrder, RhinoResourceName } from '@rhino-project/core';
import type { ColumnDisplayConfig } from '@rhino-project/ui-heroui';

type PlanningSeasonData = {
  id: number | string;
  status: string;
  rating_avg: number;
  roster_accepted: number;
  season: { name: string };
};

// Generic over the model it lists so the order terms are checked against that
// model's surface rather than against every model at once.
type DashboardTableProps<M extends RhinoResourceName> = {
  title: string;
  link: string;
  model: M;
  paths: string[];
  columnConfig?: Record<string, ColumnDisplayConfig>;
  defaultOrder?: RhinoOrder<M>;
  defaultLimit?: number;
};

const DashboardTable = <M extends RhinoResourceName>({
  title,
  link,
  paths,
  columnConfig,
  ...props
}: DashboardTableProps<M>) => {
  const baseOwnerPath = useBaseOwnerPath();
  const fullPath = baseOwnerPath.build(link);

  return (
    <Card>
      <Card.Header className="flex-row justify-between items-center">
        <Card.Title>{title}</Card.Title>
        <Link to={fullPath}>
          <Icon icon="bi:arrow-right-circle" className="text-xl text-muted" />
        </Link>
      </Card.Header>
      <Card.Content>
        <ModelIndexSimple analyticsSurface="plan_dashboard" {...props}>
          <ModelIndexTable
            paths={paths}
            columnConfig={columnConfig}
            rowLinkBase={fullPath}
          />
        </ModelIndexSimple>
      </Card.Content>
    </Card>
  );
};

const PlanningSeasonCard = ({
  season
}: {
  season: {
    id: number | string;
    status: string;
    rating_avg: number;
    roster_accepted: number;
    season: { name: string };
  };
}) => {
  const { t } = useTranslation();

  const owner = String(useBaseOwnerId());

  return (
    <Card>
      <Card.Header className="flex-row justify-between items-center">
        <Card.Title>{season.season.name}</Card.Title>
      </Card.Header>
      <Card.Content>
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-accent">
              {season.rating_avg || '-'}
            </p>
            <p className="text-xs text-muted uppercase tracking-wide">
              {t('frontend.messages.avg_rating')}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-accent">
              {season.roster_accepted}
            </p>
            <p className="text-xs text-muted uppercase tracking-wide">
              {t('frontend.messages.commits')}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <Link
            to="/$owner/planning/$team_season_id"
            params={{ owner, team_season_id: String(season.id) }}
            className="text-sm text-accent hover:underline"
          >
            {t('frontend.messages.view_roster')}
          </Link>
        </div>
      </Card.Content>
    </Card>
  );
};

const PlanDashboardPage = () => {
  const { t } = useTranslation();

  const state = useOverlayState();
  const baseOwner = useBaseOwner() as { name: string } | null;
  const team = useCurrentTeam();
  const isAdmin = useHasRoleOf('admin');
  const { resources: planningSeasons } = usePlanningSeasons();

  return (
    <BaseAuthedPage>
      <MaxWidth>
        <div className="flex justify-between items-start mb-8">
          <div>
            <Typography.Heading level={1}>
              {t('frontend.messages.planning_scouting')}
            </Typography.Heading>
            <p className="text-muted">
              {baseOwner?.name} &mdash; {team?.name}
            </p>
          </div>
          {isAdmin && (
            <Button variant="primary" onPress={state.open}>
              {t('frontend.messages.add_season')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {planningSeasons?.results?.map((season) => (
            <PlanningSeasonCard
              key={String(season.id)}
              season={season as PlanningSeasonData}
            />
          ))}
          {(!planningSeasons?.results ||
            planningSeasons.results.length === 0) && (
            <Card className="p-6 col-span-full text-center text-muted">
              {t('frontend.messages.no_planning_seasons')}
            </Card>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DashboardTable
            title={t('frontend.messages.latest_reports')}
            link="/reports"
            model="report"
            paths={[
              'report_type.name',
              'player.display_name',
              'reported_at',
              'reporter.name'
            ]}
            columnConfig={{
              'report_type.name': { width: 1.5 },
              'player.display_name': { width: 1.5 },
              reported_at: { width: 2 },
              'reporter.name': { width: 1.5 }
            }}
            defaultOrder={['-reported_at']}
            defaultLimit={5}
          />
          <DashboardTable
            title={t('frontend.messages.recent_athletes')}
            link="/players"
            model="player"
            paths={['display_name', 'dob_year', 'quick_rating']}
            defaultOrder={['-created_at']}
            defaultLimit={10}
          />
        </div>
      </MaxWidth>
      <SeasonSidebarForm isOpen={state.isOpen} onModalClose={state.close} />
    </BaseAuthedPage>
  );
};

export default PlanDashboardPage;
